import { useState, useEffect } from "react";
import {
  GridColDef,
  GridRenderCellParams,
  GridPreProcessEditCellProps,
} from "@mui/x-data-grid-pro";
import {
  Select,
  SelectChangeEvent,
  MenuItem,
  FormControl,
  Checkbox,
  OutlinedInput,
  ListItemText,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { isFulfilled } from "@reduxjs/toolkit";
import { useAppDispatch } from "../../../app/hooks";
import { DataGrid } from "../helpers/DataGrid";
import { ExpenseType, Account, IncomeType } from "../settingsApi";
import { fetchAccounts, fetchIncomeTypes } from "../settingsActions";
import {
  PRICING_CATEGORIES,
  handleAccountsData,
  handleIncomeTypesData,
} from "../helpers/utils";
import { ToggleBooleanCell } from "../helpers/ToggleBooleanCell";

type Props = {
  expenseTypes: ExpenseType[];
  onSave: (expenseTypes: ExpenseType[]) => void;
  onUndo?: () => void;
};

export const ExpenseTypesGrid = ({ expenseTypes, onSave, onUndo }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("settings");

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [incomeTypes, setIncomeTypes] = useState<IncomeType[]>([]);

  const validateCode = (code: string) => {
    const lengthNotValid = code.length < 3 || code.length > 64;
    const accountCodePattern = /^[a-zA-Z0-9-_åÅäÄöÖ]+$/;
    const patternNotValid = !code.match(accountCodePattern);
    if (lengthNotValid) {
      return t("validationExpenseTypeCodeLength");
    } else if (patternNotValid) {
      return t("codeCannotContainSpecialCharacters");
    }
    return null;
  };

  const validateName = (name: string) => {
    const lengthNotValid = name.length < 3 || name.length > 128;
    return lengthNotValid ? t("validationExpenseTypeNameLength") : null;
  };

  const preProcessCodeCellProps = (params: GridPreProcessEditCellProps) => {
    const errorMessage = validateCode(params.props.value!.toString());
    return { ...params.props, error: errorMessage };
  };

  const preProcessNameCellProps = (params: GridPreProcessEditCellProps) => {
    const errorMessage = validateName(params.props.value!.toString());
    return { ...params.props, error: errorMessage };
  };

  const startEditMode = (params: GridRenderCellParams) => {
    if (params.api.getRowMode(params.id) === "edit") return;
    params.api.startRowEditMode({ id: params.id });
  };

  const stopEditMode = (params: GridRenderCellParams) => {
    if (params.api.getRowMode(params.id) === "view") return;
    params.api.stopRowEditMode({ id: params.id });
  };

  const updateRow = (params: GridRenderCellParams) => {
    params.row.status = "updated";
    params.api.updateRows([params.row]);
  };

  const codeLabel: string = t("code");
  const nameFiLabel: string = t("nameFi");
  const nameEnLabel: string = t("nameEn");
  const nameSvLabel: string = t("nameSv");
  const debitLabel: string = t("accountDebit");
  const creditLabel: string = t("accountCredit");
  const categoryLabel: string = t("pricingCategory");
  const incomeTypeLabel: string = t("incomeType");
  const holdingTaxLabel: string = t("withHoldingTax");
  const pensionInsuranceLabel: string = t("pensionInsurance");
  const accidentInsuranceLabel: string = t("accidentInsurance");
  const unemploymentInsuranceLabel: string = t("unemploymentInsurance");
  const healthInsuranceLabel: string = t("healthInsurance");

  const columns: GridColDef[] = [
    {
      field: "code",
      headerName: codeLabel,
      width: 180,
      editable: true,
      preProcessEditCellProps: preProcessCodeCellProps,
    },
    {
      field: "name",
      headerName: nameFiLabel,
      width: 180,
      editable: true,
      preProcessEditCellProps: preProcessNameCellProps,
    },
    {
      field: "nameEN",
      headerName: nameEnLabel,
      width: 180,
      editable: true,
      preProcessEditCellProps: preProcessNameCellProps,
    },
    {
      field: "nameSV",
      headerName: nameSvLabel,
      width: 180,
      editable: true,
      preProcessEditCellProps: preProcessNameCellProps,
    },
    {
      field: "accountDebit",
      headerName: debitLabel,
      width: 250,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <FormControl fullWidth>
          <Select
            value={params.row.accountDebit ?? ""}
            label={debitLabel}
            onOpen={() => startEditMode(params)}
            onChange={(event: SelectChangeEvent) => {
              params.row.accountDebit = event.target.value as string;
              updateRow(params);
            }}
            onClose={() => {
              stopEditMode(params);
            }}
          >
            <MenuItem value={""}>{t("noSelection", { ns: "common" })}</MenuItem>
            {accounts.map((account) => (
              <MenuItem value={account.accountCode} key={account.id}>
                {account.accountCode} {account.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      field: "accountCredit",
      headerName: creditLabel,
      width: 250,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <FormControl fullWidth>
          <Select
            value={params.row.accountCredit ?? ""}
            label={creditLabel}
            onOpen={() => startEditMode(params)}
            onChange={(event: SelectChangeEvent) => {
              params.row.accountCredit = event.target.value as string;
              updateRow(params);
            }}
            onClose={() => {
              stopEditMode(params);
            }}
          >
            <MenuItem value={""}>{t("noSelection", { ns: "common" })}</MenuItem>
            {accounts.map((account) => (
              <MenuItem value={account.accountCode} key={account.id}>
                {account.accountCode} {account.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      field: "pricingCategory",
      headerName: categoryLabel,
      width: 180,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <FormControl fullWidth>
          <Select
            value={params.row.pricingCategory ?? ""}
            label={categoryLabel}
            onOpen={() => startEditMode(params)}
            onChange={(event: SelectChangeEvent) => {
              params.row.pricingCategory = event.target.value as string;
              updateRow(params);
            }}
            onClose={() => {
              stopEditMode(params);
            }}
          >
            <MenuItem value={""}>{t("noSelection", { ns: "common" })}</MenuItem>
            {PRICING_CATEGORIES.map((pricingCategory) => (
              <MenuItem value={pricingCategory.id} key={pricingCategory.id}>
                {t(pricingCategory.id)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      field: "incomeTypeCodes",
      headerName: incomeTypeLabel,
      width: 180,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <FormControl sx={{ m: 1, width: 300 }}>
          <Select
            multiple
            value={params.value}
            onOpen={() => startEditMode(params)}
            onChange={(event: SelectChangeEvent<string[]>) => {
              const {
                target: { value },
              } = event;

              let list = typeof value === "string" ? value.split(",") : value;
              params.row.incomeTypeCodes = list;
              params.value = list;
              updateRow(params);
            }}
            onClose={() => {
              stopEditMode(params);
            }}
            input={<OutlinedInput label="Tag" />}
            renderValue={(selected) => selected.join(", ")}
          >
            {incomeTypes.map((incomeType) => (
              <MenuItem key={incomeType.id} value={incomeType.id}>
                <Checkbox
                  checked={params.value.indexOf(incomeType.incomeCode) > -1}
                />
                <ListItemText
                  primary={`${incomeType.incomeCode} ${incomeType.name}`}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      field: "withHoldingTax",
      headerName: holdingTaxLabel,
      type: "boolean",
      width: 150,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <ToggleBooleanCell params={params} />
      ),
    },
    {
      field: "pensionInsurance",
      headerName: pensionInsuranceLabel,
      type: "boolean",
      width: 150,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <ToggleBooleanCell params={params} />
      ),
    },
    {
      field: "accidentInsurance",
      headerName: accidentInsuranceLabel,
      type: "boolean",
      width: 150,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <ToggleBooleanCell params={params} />
      ),
    },
    {
      field: "unemploymentInsurance",
      headerName: unemploymentInsuranceLabel,
      type: "boolean",
      width: 150,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <ToggleBooleanCell params={params} />
      ),
    },
    {
      field: "healthInsurance",
      headerName: healthInsuranceLabel,
      type: "boolean",
      width: 150,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <ToggleBooleanCell params={params} />
      ),
    },
  ];

  // on save to server, use code as id value
  const newRow: ExpenseType = {
    name: "",
    nameEN: "",
    nameSV: "",
    accountDebit: null,
    accountCredit: null,
    pricingCategory: null,
    incomeTypeCodes: [],
    withHoldingTax: false,
    pensionInsurance: false,
    accidentInsurance: false,
    unemploymentInsurance: false,
    healthInsurance: false,
  };

  const rows = expenseTypes.map((expenseType) => {
    return {
      code: expenseType.id,
      ...expenseType,
    };
  });

  useEffect(() => {
    dispatch(fetchAccounts({})).then((action) => {
      if (isFulfilled(action)) {
        const array = handleAccountsData(action.payload.accounts);
        setAccounts(array);
      }
    });
    dispatch(fetchIncomeTypes({})).then((action) => {
      if (isFulfilled(action)) {
        const array = handleIncomeTypesData(action.payload.incomeTypes);
        setIncomeTypes(array);
      }
    });
  }, [dispatch]);

  return (
    <DataGrid
      columns={columns}
      rows={rows}
      newRow={newRow}
      hideSave={false}
      onSave={onSave}
      onUndo={onUndo}
      actions={["delete"]}
    />
  );
};
